import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";
import { stashReferral, readReferral } from "@/lib/referralStash";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    community_guidelines_accepted: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  // Check for referral code in URL (fallback: sessionStorage stashed by TrackPage)
  useEffect(() => {
    const ref = searchParams.get('ref') || readReferral();
    if (ref) {
      // Phase 15.5 P5: stash so it survives the external Google OAuth redirect.
      stashReferral(ref);
      setReferralCode(ref);
      setIsLogin(false); // Switch to register mode for referrals
      // Validate referral code and get referrer info
      axios.get(`${API}/referral/validate/${ref}`)
        .then(response => {
          if (response.data.valid) {
            setReferrer(response.data.referrer);
            toast.success(`Referred by ${response.data.referrer.artist_name || response.data.referrer.name}!`);
          }
        })
        .catch(() => {
          // Invalid referral code, just ignore
          setReferralCode(null);
        });
    }
  }, [searchParams]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success("Check your email for reset instructions");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      toast.success("If an account exists, you'll receive reset instructions");
      setShowForgotPassword(false);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !formData.community_guidelines_accepted) {
      toast.error("Please accept the Community Guidelines to continue");
      return;
    }
    
    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // Set cookie with proper attributes for production
        const isSecure = window.location.protocol === 'https:';
        const cookieString = `session_token=${response.data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=${isSecure ? 'None; Secure' : 'Lax'}`;
        document.cookie = cookieString;
        
        // Also store in localStorage as backup
        localStorage.setItem('session_token', response.data.session_token);
        toast.success("Welcome back!");

        // Phase 5: honor pending crew invite stashed in sessionStorage
        // Phase 15.5 P1: honor ?next= redirect (e.g. login-then-/submit-beat)
        let nextRoute = "/dashboard";
        const nextParam = searchParams.get("next");
        if (nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")) {
          // Only allow same-origin relative paths to avoid open-redirect.
          nextRoute = nextParam;
        }
        try {
          const stash = sessionStorage.getItem('rgm_crew_invite');
          if (stash) {
            const parsed = JSON.parse(stash);
            if (parsed && parsed.crewId && parsed.code) {
              nextRoute = `/crews/${parsed.crewId}?invite=${encodeURIComponent(parsed.code)}`;
            }
          }
        } catch { /* ignore */ }
        navigate(nextRoute, { state: { user: response.data.user } });
      } else {
        await axios.post(`${API}/auth/register`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          community_guidelines_accepted: formData.community_guidelines_accepted,
          referral_code: referralCode || null
        });
        toast.success("Account created! Please login.");
        setIsLogin(true);
        setFormData({ ...formData, password: "", community_guidelines_accepted: false });
        setReferralCode(null);
        setReferrer(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Phase 15.5 P1: persist ?next= across the external Google OAuth redirect.
    // The Emergent auth provider strips query params on round-trip, so we
    // stash a same-origin relative path in sessionStorage and AuthCallback
    // honors it post-login.
    try {
      const nextParam = searchParams.get('next');
      if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
        sessionStorage.setItem('rgm_oauth_next', nextParam);
      } else {
        sessionStorage.removeItem('rgm_oauth_next');
      }
    } catch { /* ignore storage errors */ }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
      <Card className="auth-card" data-testid="auth-card">
        <div className="mb-6 text-center">
          <Link to="/" className="inline-flex items-center justify-center">
            <img src="/rgm-logo.png" alt="RGM Studio" className="h-12 w-auto object-contain mx-auto" />
          </Link>
          <p className="text-gray-400 mt-2">{isLogin ? "Welcome back" : "Create your account"}</p>
          
          {/* Referral Banner */}
          {referrer && !isLogin && (
            <div className="mt-4 p-3 bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg border border-purple-500/30" data-testid="referral-banner">
              <div className="flex items-center justify-center gap-2">
                {(referrer.profile_photo || referrer.picture) && (
                  <img 
                    src={getProfilePhotoUrl(referrer)}
                    alt={referrer.artist_name || referrer.name}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <span className="text-sm text-gray-300">
                  Referred by <span className="text-purple-400 font-semibold">{referrer.artist_name || referrer.name}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          {!isLogin && (
            <Input
              data-testid="input-name"
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="auth-input"
            />
          )}
          
          <Input
            data-testid="input-email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="auth-input"
          />
          
          <Input
            data-testid="input-password"
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            className="auth-input"
          />

          {!isLogin && (
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="guidelines"
                checked={formData.community_guidelines_accepted}
                onChange={(e) => setFormData({ ...formData, community_guidelines_accepted: e.target.checked })}
                className="w-5 h-5 mt-1"
                data-testid="guidelines-checkbox"
              />
              <label htmlFor="guidelines" className="text-gray-300 text-sm">
                I have read and agree to the{" "}
                <Link to="/community-guidelines" className="text-purple-400 hover:text-purple-300 underline" target="_blank">
                  Community Guidelines
                </Link>
                ,{" "}
                <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline" target="_blank" data-testid="signup-terms-link">
                  Terms of Service
                </Link>
                , and{" "}
                <Link to="/privacy" className="text-purple-400 hover:text-purple-300 underline" target="_blank" data-testid="signup-privacy-link">
                  Privacy Policy
                </Link>
                .
              </label>
            </div>
          )}

          <Button
            data-testid="submit-auth-button"
            type="submit"
            disabled={isLoading}
            className="w-full auth-submit-btn"
          >
            {isLoading ? "Processing..." : (isLogin ? "Login" : "Sign Up")}
          </Button>
        </form>

        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-gray-600"></div>
          <span className="px-4 text-gray-400 text-sm">or</span>
          <div className="flex-1 border-t border-gray-600"></div>
        </div>

        <Button
          data-testid="google-login-button"
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full google-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="mt-6 text-center space-y-3">
          <button
            data-testid="toggle-auth-mode"
            onClick={() => setIsLogin(!isLogin)}
            className="text-purple-400 hover:text-purple-300 text-sm"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Login"}
          </button>
          
          {isLogin && (
            <div>
              <button
                data-testid="forgot-password-link"
                onClick={() => setShowForgotPassword(true)}
                className="text-gray-400 hover:text-purple-300 text-sm"
              >
                Forgot your password?
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowForgotPassword(false)}>
          <Card 
            className="w-full max-w-md p-6" 
            style={{ background: 'rgba(26, 26, 30, 0.98)', borderColor: 'rgba(138, 43, 226, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="forgot-password-modal"
          >
            <h3 className="text-xl font-bold text-white mb-2">Reset Password</h3>
            <p className="text-gray-400 mb-6 text-sm">Enter your email address and we'll send you instructions to reset your password.</p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                data-testid="forgot-password-email"
                type="email"
                placeholder="Email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                className="auth-input"
              />
              
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  data-testid="forgot-password-submit"
                >
                  {resetLoading ? "Sending..." : "Send Reset Link"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  variant="outline"
                  className="flex-1 text-white border-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LoginPage;